// Saving new levels for a shared capability. With other levels it is no longer
// the capability the other roles hold: the manager has used it as a starting
// point, and what this role has now is its own. So it must be renamed before
// it is saved, and the other roles keep the one they shared, as it was
// (see ./sharedCapabilities.ts).

import React, { useState } from 'react';
import { Box, Dialog, IconButton, InputBase, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MAX_NAME, type SetupRole } from './capabilitySetup';
import { ActionButton, BODY, BRANDY, DANGER, FONT, LABEL, LINE, SECTION_TITLE, TEXT, TEXT_2 } from './designSystem';
import { ownNameProblem, withOwnNames, type SharedEdit } from './sharedCapabilities';

/** "A", "A and B", "A, B and C". */
const listed = (names: string[]) =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const SharedCapabilityDialog: React.FC<{
  /** The shared capabilities whose levels the edits change; none closes the dialog. */
  edits: SharedEdit[];
  /** The role being saved, with its unsaved edits. */
  role: SetupRole;
  /** Every role as last saved. */
  roles: SetupRole[];
  onClose: () => void;
  /** Every one has a name of its own, by the capability's id on this role. */
  onRename: (names: Record<string, string>) => void;
}> = ({ edits, role, roles, onClose, onRename }) => {
  // The names typed so far; one not typed yet still reads as the shared name, which is refused.
  const [names, setNames] = useState<Record<string, string>>({});
  const nameOf = (edit: SharedEdit) => names[edit.after.id] ?? edit.after.name;
  // The role as it would be saved, every new name in: two capabilities given one name refuse each other.
  const named = withOwnNames(role, Object.fromEntries(edits.map((edit) => [edit.after.id, nameOf(edit)])));
  const problemOf = (edit: SharedEdit) => ownNameProblem(roles, named, edit, nameOf(edit));
  const ready = edits.every((edit) => !problemOf(edit));
  const close = () => {
    setNames({});
    onClose();
  };

  return (
    <Dialog
      open={edits.length > 0}
      onClose={close}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 600,
          maxWidth: '94vw',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#1C1C1C',
          backgroundImage: 'none',
          border: `1px solid ${LINE}`,
          borderRadius: '8px'
        }
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          p: '16px 20px',
          borderBottom: `1px solid ${LINE}`
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...SECTION_TITLE, whiteSpace: 'normal' }}>
            {edits.length > 1 ? 'Rename these capabilities' : 'Rename this capability'}
          </Typography>
          <Typography sx={{ ...BODY, fontSize: 12.5, mt: '4px' }}>
            You edited the levels of {edits.length > 1 ? 'capabilities' : 'a capability'} that {role.name} shares with
            other roles. {role.name} now gets its own {edits.length > 1 ? 'versions' : 'version'}, so give{' '}
            {edits.length > 1 ? 'these capabilities new names' : 'this capability a new name'}.
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={close} sx={{ color: TEXT_2, '&:hover': { color: TEXT } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '4px 20px 16px', scrollbarWidth: 'thin' }}>
        {edits.map((edit, i) => {
          const { id } = edit.after;
          const others = listed(edit.roles.map((r) => r.name));
          const typed = id in names;
          const problem = problemOf(edit);
          return (
            <Box key={id} sx={{ pt: '14px' }}>
              <Typography sx={{ ...LABEL, color: TEXT, pb: '6px', borderBottom: '1px solid rgba(247, 148, 32, 0.45)' }}>
                {edit.before.name}
              </Typography>
              <Typography sx={{ ...BODY, fontSize: 12.5, py: '8px' }}>
                {others} {edit.roles.length === 1 ? 'keeps' : 'keep'} {edit.before.name} with its original levels.
                People in {role.name} keep the level they already have, marked &quot;Scored on earlier levels&quot;,
                until their next Roleplay is scored on the new levels.
              </Typography>
              <Typography sx={{ ...LABEL, mb: '4px' }}>New Name For {role.name}</Typography>
              <InputBase
                autoFocus={i === 0}
                value={nameOf(edit)}
                onChange={(e) => setNames((n) => ({ ...n, [id]: e.target.value.slice(0, MAX_NAME) }))}
                inputProps={{ 'aria-label': `New name for ${edit.before.name} on ${role.name}`, maxLength: MAX_NAME }}
                sx={{
                  width: '100%',
                  height: 34,
                  px: '10px',
                  fontFamily: FONT,
                  fontSize: 13.5,
                  color: TEXT,
                  border: `1px solid ${typed && problem ? DANGER : '#3A3A3A'}`,
                  borderRadius: '4px',
                  '&.Mui-focused': { borderColor: typed && problem ? DANGER : BRANDY }
                }}
              />
              {/* Before anything is typed the name is still the shared one: say what is needed, not that it is wrong. */}
              {problem && (
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: typed ? DANGER : TEXT_2, mt: '4px' }}>
                  {problem}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
          p: '12px 20px',
          borderTop: `1px solid ${LINE}`
        }}
      >
        <ActionButton onClick={close}>Cancel</ActionButton>
        <ActionButton
          primary
          disabled={!ready}
          reason="Give each one a name of its own first."
          onClick={() => {
            onRename(Object.fromEntries(edits.map((edit) => [edit.after.id, nameOf(edit)])));
            setNames({});
          }}
        >
          {edits.length > 1 ? 'Rename Capabilities' : 'Rename Capability'}
        </ActionButton>
      </Box>
    </Dialog>
  );
};

export default SharedCapabilityDialog;
